import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { adminApi } from '../services/adminApi';
import { isTestModeBypass } from '../services/testMode';
import type { AdminUser, UserRole } from '../types/admin';
import { captureLoginOncePerDay, setBranchContext, setUserSegment } from '../services/posthog';

interface UserRoleContextType {
  // All roles that apply to the current user at their branch. A user may hold several
  // (e.g. ['agent','agent-in-charge']); there is no single canonical role.
  roles: UserRole[];
  hasRole: (role: UserRole) => boolean;
  isAdmin: boolean;
  isSuperUser: boolean;
  isCorporate: boolean;
  isAgentInCharge: boolean;
  canEditContent: boolean;
  isNewGrad: boolean;
  // Franchise owner mid-onboarding: adds "My Onboarding" to the nav and a tile on Home pointing to
  // it (WIPE-1645). Gates nothing else.
  isOnboarding: boolean;
  // Applied Epic self-service. Not derived from `roles` at all: it is a per-user grant on the
  // user record (`self_service_access`), and no role — admin included — stands in for it, which
  // mirrors the 'SelfService' capability the API gates on. Anything that hides or shows the
  // feature reads this, so the nav, the route guard and the API all answer from one rule.
  canUseSelfService: boolean;
  // The Comparative Rater, on the same footing and for a different reason: a submission spends the
  // agency's carrier credentials, so the `rater-access` grant names who may do that one user at a time.
  // No account role confers it either. Mirrors the 'Rater' capability the API gates on.
  canUseRater: boolean;
  // The property-data evaluation inside the Rater: choosing the data sources and comparing their
  // answers. The `rater-data-eval` grant, effective only with rater access. Mirrors the
  // 'RaterEvaluation' capability the API gates on.
  canEvaluateRaterData: boolean;
  // The quote tools (WIPE-1956): attaching a client's quotes in Ask AgencyOS. The `quote-review` grant, and
  // admin does not stand in for it. Mirrors the 'QuoteReview' capability the API gates on.
  canReviewQuotes: boolean;
  // Editing the in-app assistant's system prompt. A grant, not a role, and admin does not stand in for
  // it: this is who decides how the assistant answers every agent in the network. Mirrors the
  // 'AssistantPrompt' capability the API gates on.
  isKnowledgeManager: boolean;
  // Opening the HR tool. A grant on the same footing as isKnowledgeManager, and the only thing that
  // decides it: neither admin nor the hr-confidential role stands in for it, so the gear menu and the
  // route guard answer from this alone.
  hasHrAccess: boolean;
  // Redirecting the marketing site's agency URLs (WIPE-1760). Unlike the grants above, admin does stand
  // in for it, so this is the agency-redirect-admin grant or an admin role. Mirrors the 'AgencyRedirects'
  // capability the API gates on.
  canManageAgencyRedirects: boolean;
  // Agency Statements: the agency's own statements and fees. The agency-statements grant only — admin
  // does not stand in for it here, though the API lets admins read any agency's, which is what lets
  // "login as" show exactly what the impersonated user sees.
  canViewAgencyStatements: boolean;
  // The Branch Management editor: an admin, while the API still lets anyone edit those fields. Once the
  // API reads them from the HubSpot Franchise record it says no (WIPE-1884), and the editor goes.
  canEditBranches: boolean;
  isLoading: boolean;
  // True when the role lookup failed, so `roles` is a fallback rather than this user's real
  // roles. Previously such a failure was indistinguishable from genuinely being an agent: the
  // catch below swallowed the error and left the default in place, so a corporate user whose
  // token couldn't be renewed silently rendered as an agent (WIPE-946). Callers that gate
  // access on a role can now tell "not permitted" from "we don't know yet".
  rolesUnknown: boolean;
}

const UserRoleContext = createContext<UserRoleContextType>({
  roles: ['agent'],
  hasRole: () => false,
  isAdmin: false,
  isSuperUser: false,
  isCorporate: false,
  isAgentInCharge: false,
  canEditContent: false,
  isNewGrad: false,
  isOnboarding: false,
  canUseSelfService: false,
  canUseRater: false,
  canEvaluateRaterData: false,
  canReviewQuotes: false,
  isKnowledgeManager: false,
  hasHrAccess: false,
  canManageAgencyRedirects: false,
  canViewAgencyStatements: false,
  canEditBranches: false,
  isLoading: true,
  rolesUnknown: false,
});

export const UserRoleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [roles, setRoles] = useState<UserRole[]>(['agent']);
  const [isNewGrad, setIsNewGrad] = useState(false);
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [selfServiceAccess, setSelfServiceAccess] = useState(false);
  const [raterAccess, setRaterAccess] = useState(false);
  const [raterDataEval, setRaterDataEval] = useState(false);
  const [knowledgeManager, setKnowledgeManager] = useState(false);
  const [hrAccess, setHrAccess] = useState(false);
  const [agencyRedirectAdmin, setAgencyRedirectAdmin] = useState(false);
  const [agencyStatements, setAgencyStatements] = useState(false);
  const [quoteReview, setQuoteReview] = useState(false);
  const [branchEditorOpen, setBranchEditorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rolesUnknown, setRolesUnknown] = useState(false);

  useEffect(() => {
    const init = async () => {
      const overrideEmail = sessionStorage.getItem('branchOverrideEmail');

      // The one place a profile becomes state. Three reads below can supply one — first login, the
      // ordinary refresh, and an admin's "login as" override — and a field read on only some of
      // them would grant a capability that disappears on the next page load.
      const applyProfile = (profile: AdminUser) => {
        setRoles(profile.roles);
        setIsNewGrad(!!profile.new_grad_program);
        setIsOnboarding(!!profile.onboarding_in_process);
        setSelfServiceAccess(!!profile.self_service_access);
        setRaterAccess(!!profile.rater_access);
        setRaterDataEval(!!profile.rater_data_eval);
        setKnowledgeManager(!!profile.knowledge_manager);
        setHrAccess(!!profile.hr_access);
        setAgencyRedirectAdmin(!!profile.agency_redirect_admin);
        setAgencyStatements(!!profile.agency_statements);
        setQuoteReview(!!profile.quote_review);
      };

      if (!isAuthenticated || !user?.email || !adminApi.isConfigured()) {
        setIsLoading(false);
        return;
      }

      // A test-mode session holds no bearer token, so every call below is behind requireAuth
      // and can only 401. Answer locally instead: the requests are unanswerable, and because
      // the guard below is only written on success they were reissued on every page load —
      // one E2E run put hundreds of 401s on the real API (WIPE-1190). Resolve the bypass
      // identity to a fixed role so the suite is deterministic rather than asserting against
      // whatever a failed lookup happens to leave behind.
      if (isTestModeBypass()) {
        setRoles(['agent']);
        setIsNewGrad(false);
        setIsOnboarding(false);
        setRolesUnknown(false);
        setIsLoading(false);
        return;
      }

      try {
        // Clear any previous failure: this attempt decides the outcome.
        setRolesUnknown(false);
        const recorded = sessionStorage.getItem('admin-login-recorded');

        // The signed-in user's own profile, resolved whether or not a branch is being
        // impersonated: it's what the analytics below describe, since the person using the app
        // is the same person either way.
        if (!recorded) {
          await adminApi.recordLogin(user.email, user.name, user.provider);
          sessionStorage.setItem('admin-login-recorded', 'true');
        }
        // Read after the login is recorded rather than taken from its answer, which has no capabilities.
        const userProfile = await adminApi.getMyProfile();
        // The signed-in user's, even while impersonating: whether the editor exists is the API's
        // setting, not something the impersonated user has. An API that predates the flag leaves it open.
        setBranchEditorOpen(userProfile?.capabilities?.branches !== false);

        // Impersonation replaces the roles the UI renders with the impersonated user's.
        let overrideProfile: AdminUser | null = null;
        if (overrideEmail) {
          overrideProfile = await adminApi.getProfileByEmail(overrideEmail);
        }
        const renderedProfile = overrideProfile ?? userProfile;
        if (renderedProfile) applyProfile(renderedProfile);

        // Branch and segment register as PostHog super properties, so they must be set before
        // the login event is captured for it to carry them.
        if (renderedProfile?.branch_id) {
          await setBranchContext(renderedProfile.branch_id, !!overrideProfile);
        }
        if (userProfile?.segment) {
          setUserSegment(userProfile.segment);
        }

        // Reached on every boot; the once-per-day guard inside is what makes the count right.
        // Deliberately not tied to `recorded` (per tab) or to recordLogin (which never succeeds
        // for a client whose token is dead — WIPE-1190).
        if (userProfile) {
          captureLoginOncePerDay({
            segment: userProfile.segment,
            provider: user.provider,
            is_impersonating: !!overrideProfile,
          });
        }
      } catch {
        // The lookup failed, so we don't know this user's roles. Flag that explicitly instead
        // of leaving the ['agent'] default to masquerade as the answer — the UI needs to be
        // able to distinguish an unknown role from a known-restricted one.
        setRolesUnknown(true);
      }

      setIsLoading(false);
    };

    init();
  }, [isAuthenticated, user]);

  const hasRole = (role: UserRole) => roles.includes(role);
  const isAdmin = hasRole('admin') || hasRole('super-user');
  const isSuperUser = hasRole('super-user');
  const isCorporate = hasRole('corporate') || hasRole('hr-confidential') || isAdmin;
  const isAgentInCharge = hasRole('agent-in-charge');
  const canEditContent = isCorporate;
  // The grant alone, with no role behind it: an admin without the flag is denied too, exactly as
  // the API denies them. Being able to set the flag is not the same as holding it.
  const canUseSelfService = selfServiceAccess;
  const canUseRater = raterAccess;
  const canEvaluateRaterData = raterAccess && raterDataEval;
  const canReviewQuotes = quoteReview;
  const isKnowledgeManager = knowledgeManager;
  const hasHrAccess = hrAccess;
  const canManageAgencyRedirects = agencyRedirectAdmin || isAdmin;
  const canViewAgencyStatements = agencyStatements;
  const canEditBranches = isAdmin && branchEditorOpen;

  return (
    <UserRoleContext.Provider value={{ roles, hasRole, isAdmin, isSuperUser, isCorporate, isAgentInCharge, canEditContent, isNewGrad, isOnboarding, canUseSelfService, canUseRater, canEvaluateRaterData, canReviewQuotes, isKnowledgeManager, hasHrAccess, canManageAgencyRedirects, canViewAgencyStatements, canEditBranches, isLoading, rolesUnknown }}>
      {children}
    </UserRoleContext.Provider>
  );
};

export const useUserRole = () => useContext(UserRoleContext);
